import { TmsName } from './tms.constants';

export type SettingsInputType = 'select' | 'text' | 'integer' | 'textarea' | 'checkbox';

export interface SettingsFieldDef {
  /** The key stored in the customer's settings dictionary. */
  key: string;
  label: string;
  inputType: SettingsInputType;
  /** TMS this setting applies to. */
  tmsName: string;
  /** Optional customer type filter ("Carrier" | "Broker"); applies to any type when omitted. */
  customerType?: string;
  /** Options for select inputs. */
  options?: { value: string; label: string }[];
  /** String stored when a checkbox is checked / unchecked (values are persisted as strings). */
  checkedValue?: string;
  uncheckedValue?: string;
  placeholder?: string;
  description?: string;
  /** Value to pre-populate when the field first becomes applicable. */
  default?: string;
}

/**
 * Settings keys shown on the customer screen, filtered by the selected TMS
 * (and customer type where relevant). Values are persisted as strings in the
 * customer's `settings` dictionary.
 */
export const SettingsFields: SettingsFieldDef[] = [
  // Legacy McLeod
  {
    key: 'mcleod-process-invoice-enabled',
    label: 'Process Invoice Enabled',
    inputType: 'checkbox',
    tmsName: TmsName.LegacyMcLeod,
    customerType: 'Broker',
    checkedValue: 'true',
    uncheckedValue: 'false',
    description: 'Enables invoice-specific processing when the document type is Invoice (ID 6).',
  },
  {
    key: 'mcleod-process-settlements-enabled',
    label: 'Process Settlements Enabled',
    inputType: 'checkbox',
    tmsName: TmsName.LegacyMcLeod,
    customerType: 'Carrier',
    checkedValue: 'true',
    uncheckedValue: 'false',
    description: 'Enables POD settlement processing when the document type is Proof of Delivery (ID 12).',
  },

  // BrokerAI
  {
    key: 'brokerai-force-header',
    label: 'Force Header',
    inputType: 'checkbox',
    tmsName: TmsName.BrokerAI,
    checkedValue: '1',
    uncheckedValue: '0',
    default: '0',
    description: 'Forces a specific header format in BrokerAI API requests. Defaults to "0".',
  },

  // TruckMate
  {
    key: 'truckmate-expand-orders-objects',
    label: 'Expand Orders Objects',
    inputType: 'text',
    tmsName: TmsName.TruckMate,
    placeholder: 'details,items',
    description: 'Related objects to expand when fetching orders (comma-separated, e.g. "details,items").',
  },

  // DMS
  {
    key: 'dms-application-id',
    label: 'Application ID',
    inputType: 'text',
    tmsName: TmsName.DMS,
    description: 'DMS application identifier.',
  },
  {
    key: 'dms-machine-name',
    label: 'Machine Name',
    inputType: 'text',
    tmsName: TmsName.DMS,
    description: 'DMS machine name.',
  },
  {
    key: 'dms-queue-id',
    label: 'Queue ID',
    inputType: 'text',
    tmsName: TmsName.DMS,
    description: 'DMS queue identifier.',
  },
  {
    key: 'dms-alias-id',
    label: 'Alias ID',
    inputType: 'text',
    tmsName: TmsName.DMS,
    description: 'DMS alias identifier.',
  },
  {
    key: 'dms-doc-class-name',
    label: 'Document Class Name',
    inputType: 'text',
    tmsName: TmsName.DMS,
    description: 'DMS document class name.',
  },
  {
    key: 'dms-token-ttl-ms',
    label: 'Token TTL (ms)',
    inputType: 'integer',
    tmsName: TmsName.DMS,
    description: 'Auth token time-to-live in milliseconds.',
  },
  {
    key: 'dms-token-retry-count',
    label: 'Token Retry Count',
    inputType: 'integer',
    tmsName: TmsName.DMS,
    description: 'Number of token fetch retries.',
  },
  {
    key: 'dms-driver-mandatory',
    label: 'Driver Mandatory',
    inputType: 'checkbox',
    tmsName: TmsName.DMS,
    checkedValue: 'true',
    uncheckedValue: 'false',
    description: 'Fail the export when no driver can be resolved for the document.',
  },
];
